<script setup lang="ts">
import { computed } from "vue";
import { companyLook, type LookFallback } from "../look";
import PinkMark from "./PinkMark.vue";

/**
 * 🔴 **THE ONE WAY A COMPANY IS DRAWN** (D164 rule 5).
 *
 * Devin: *"WHENEVER an agency (any company) is shown, show it in its own colours, logo and font via ONE shared
 * badge component."* An actor can be represented by several agencies at once, and each deal says which one it
 * came through — so the same agency is named in the switcher, on a person card and on her own pages, and it
 * must be the same object in all three. Every Vue surface that names a company uses this; `markCell.ts` is the
 * grid's equivalent and reads the same `companyLook`.
 *
 * The parts: `PinkMark` (their logo on the D139 plate, or their monogram), the name set in their own face
 * (`fontStack(brandFont)` — offered to the browser, never fetched), an optional caption, and an accent edge.
 *
 * 🔴 **NEVER GREY.** The accent is their palette; failing that the accent `useMarks` read off their logo
 * (`derived`); failing that their name's `PinkFace` tint. The tenant's fallback is never reached, because this
 * component always knows the name. Nothing is written back to `brandColours` — a person takes a look (D102).
 *
 * This component does not fetch: the caller's `useMarks` hands it the object URL and the derived accent.
 * 🔴 A picture a human looks at — nothing ranks, filters or sorts on it (D15).
 */
export interface CompanyBadgeSubject {
  /** The company's public id (D39). */
  id: string;
  name: string;
  logoSha256?: string | null;
  logoBackground?: string | null;
  brandColours?: readonly string[] | null;
  brandFont?: string | null;
}

/** The mark's edge at each size: a list row, a card line, the head of a page. */
const MARK_SIZES = { sm: 24, md: 32, lg: 48 } as const;

const props = withDefaults(
  defineProps<{
    organisation: CompanyBadgeSubject;
    size?: keyof typeof MARK_SIZES;
    /** A line under the name — "Film & TV", "Paid through". The `caption` slot wins over it. */
    caption?: string | null;
    /** An object URL from the caller's `useMarks`, or null while there is none. */
    src?: string | null;
    /** The accent `useMarks` read off their logo, below their palette. */
    derived?: string | null;
    /** The mark alone, titled with the name — a collapsed rail has no room for words. */
    markOnly?: boolean;
  }>(),
  { size: "md", caption: null, src: null, derived: null, markOnly: false },
);

const fallback = computed<LookFallback>(() => ({ derived: props.derived, name: props.organisation.name }));
const look = computed(() => companyLook(props.organisation, fallback.value));
const nameStyle = computed(() => (look.value.font === null ? {} : { fontFamily: look.value.font }));
</script>

<template>
  <span
    class="pk-cobadge"
    :class="[`pk-cobadge--${props.size}`, { 'pk-cobadge--mark-only': props.markOnly }]"
    :style="look.style"
    :title="props.markOnly ? props.organisation.name : undefined"
    :data-look-basis="look.basis"
    data-testid="company-badge"
  >
    <PinkMark
      :name="props.organisation.name"
      :look="props.organisation"
      :src="props.src"
      :size="MARK_SIZES[props.size]"
      :look-fallback="fallback"
      title=""
    />
    <span v-if="!props.markOnly" class="pk-cobadge__text">
      <span class="pk-cobadge__name" :style="nameStyle" data-testid="company-badge-name">{{ props.organisation.name }}</span>
      <span v-if="$slots.caption || props.caption" class="pk-cobadge__caption"><slot name="caption">{{ props.caption }}</slot></span>
    </span>
  </span>
</template>
